import { z } from 'zod';
import { DOCUMENTOS_ACLARABLES, PRONOSTICOS, TIPOS_DE_NOTA } from '../catalogos/documentos.js';
import { esquemaId, esquemaTextoClinico } from './comunes.js';
/**
 * Notas clinicas.
 *
 * Tres tipos, y el tercero no es como los otros dos. Una nota de evolucion y una
 * de egreso son documentos que se emiten y se firman; una nota de **aclaracion**
 * existe para corregir algo ya asentado SIN tocarlo. Por eso no comparten
 * esquema: la aclaratoria obliga a decir a que documento se refiere, y las otras
 * dos no admiten ese campo.
 *
 * La estructura de campos sigue lo que pide la NOM-004 para consulta externa. La
 * correspondencia campo por campo se documenta aparte, en `docs/VALIDACION.md`,
 * donde puede revisarla quien firma.
 */
const fechaClinica = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD');
/**
 * Nota de evolucion.
 *
 * El cuerpo de lo que se asienta en cada consulta de seguimiento.
 */
export const esquemaCrearNotaEvolucion = z.object({
    pacienteId: esquemaId,
    fecha: fechaClinica,
    /** Lo que la paciente refiere. */
    padecimiento: z.string().trim().min(3, 'Escriba la evolución del padecimiento').max(4000),
    exploracion: esquemaTextoClinico(4000).default(''),
    /** Resultados de estudios de laboratorio y gabinete que se revisaron hoy. */
    resultadosEstudios: esquemaTextoClinico(4000).default(''),
    diagnosticos: z.string().trim().min(3, 'Asiente el diagnóstico').max(2000),
    tratamiento: esquemaTextoClinico(4000).default(''),
    pronostico: z.enum(PRONOSTICOS).default('Bueno'),
    /** El estudio del que sale esta nota, si sale de uno. */
    colposcopiaId: esquemaId.optional(),
});
/**
 * Nota de egreso.
 *
 * Cierra un episodio de atencion. En un consultorio de colposcopia lo habitual
 * es cerrar el seguimiento de una lesion, no un internamiento.
 */
export const esquemaCrearNotaEgreso = z.object({
    pacienteId: esquemaId,
    fecha: fechaClinica,
    fechaInicioAtencion: fechaClinica,
    motivoEgreso: z.string().trim().min(3, 'Diga por qué se egresa').max(500),
    diagnosticosFinales: z.string().trim().min(3, 'Asiente los diagnósticos finales').max(2000),
    resumenEvolucion: z.string().trim().min(3, 'Resuma la evolución').max(6000),
    manejo: esquemaTextoClinico(4000).default(''),
    /** Lo que queda abierto: es lo que evita que un seguimiento se pierda. */
    problemasPendientes: esquemaTextoClinico(2000).default(''),
    planDeManejo: esquemaTextoClinico(4000).default(''),
    recomendacionesVigilancia: esquemaTextoClinico(2000).default(''),
    pronostico: z.enum(PRONOSTICOS).default('Bueno'),
});
/**
 * Nota de aclaracion.
 *
 * Un documento firmado no se corrige por encima: se aclara. Esta nota apunta al
 * documento que aclara y **no lo modifica**; las dos cosas quedan en el
 * expediente, y quien lo lea despues ve que se dijo, que se aclaro y cuando.
 *
 * Es el unico camino para arreglar lo que ya no admite version nueva: una receta
 * entregada, una nota de egreso cerrada.
 */
export const esquemaCrearNotaAclaratoria = z.object({
    pacienteId: esquemaId,
    fecha: fechaClinica,
    /** Que documento se aclara. Obligatorio: una aclaracion sin referente no aclara nada. */
    documentoTipo: z.enum(DOCUMENTOS_ACLARABLES),
    documentoId: esquemaId,
    /** Que decia, en las palabras de quien aclara. */
    loAsentado: z.string().trim().min(3, 'Diga qué se asentó').max(2000),
    /** Que debe entenderse. */
    aclaracion: z.string().trim().min(10, 'Escriba la aclaración').max(4000),
    motivo: z.string().trim().min(5, 'Diga por qué se aclara').max(500),
});
/** Correccion de una nota ya firmada: sube de version y se vuelve a firmar. */
const motivoDeEdicion = {
    motivoEdicion: z.string().trim().min(5, 'Explique qué se corrige y por qué').max(300),
};
export const esquemaActualizarNotaEvolucion = esquemaCrearNotaEvolucion
    .omit({ pacienteId: true })
    .partial()
    .extend(motivoDeEdicion);
export const esquemaActualizarNotaEgreso = esquemaCrearNotaEgreso
    .omit({ pacienteId: true })
    .partial()
    .extend(motivoDeEdicion);
/**
 * Una aclaracion NO se corrige.
 *
 * Si estuvo mal, se emite otra. Permitir editarla convertiria el mecanismo de
 * correccion en algo que tambien hay que auditar, y no habria donde parar.
 */
export const esquemaListarNotas = z.object({
    pacienteId: esquemaId.optional(),
    tipo: z.enum(TIPOS_DE_NOTA).optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(100).default(20),
});
//# sourceMappingURL=nota.js.map